import { useState, useEffect, useRef } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import ViewShot from 'react-native-view-shot'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import { useAuth } from '../../lib/AuthContext'
import { cacheGet, cacheSet, CACHE_KEY } from './cache'
import { EmptyState, SectionLabel, medal } from './ui'
import { Podio, ListaClassifica, MyRank, tiedPositions, posOf, toSeconds } from './Classifica'
import SegmentedTabs from '../SegmentedTabs'
import { ListSkeleton, ErrorState, StaleBanner } from '../StateViews'
import ClassificaShareCard, { SHARE_W } from './ClassificaShareCard'
import { shareViewAsImage } from '../../lib/shareImage'
import { fmtGap } from '../Pick/odrResults'
import { legibleOn, textOn } from '../../lib/contrast'
import LeaderHero from './LeaderHero'
import { perfMark, perfClear } from '../../lib/perfTrace'
import GradientButton from '../GradientButton'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
// Classifica a punti con i pari merito: chi ha gli stessi punti ha la stessa posizione (1, 2, 2, 4...)
function rankByPoints(rows, valueOf) {
  const sorted = [...rows].sort((a, b) => valueOf(b) - valueOf(a))
  return sorted.map(r => ({ ...r, posizione_classifica: 1 + sorted.filter(o => valueOf(o) > valueOf(r)).length }))
}

// Tempo/distacco: "00:00:12" -> "+0:12"; il primo (distacco zero) "0:00"
const fmtTime = (g) => fmtGap(g) || '0:00'

export default function ClassificaTour({ idStage, stageNumber, prevStageId, leagueId, colors: raceColors, currentUserId, onPlayerClick, initialScope, raceName }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const styles = makeStyles(tc)
  const { leagueName } = useAuth()

  const [scope, setScope] = useState(initialScope ?? 'tappa')
  const [type, setType] = useState('tempo')
  const [data, setData] = useState({ tempoT: [], tempoG: [], puntiT: [], puntiG: [], gpmT: [], gpmG: [] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)
  const [sharing, setSharing] = useState(false)

  const viewShotRef = useRef(null)

  useEffect(() => {
    if (!idStage || !leagueId) return
    let cancelled = false
    const load = async () => {
      perfClear(['tour', 'list', 'avatars']); perfMark('tour_start')
      const cacheKey = CACHE_KEY(leagueId, idStage, 'tour2')
      const cached = await cacheGet(cacheKey)
      if (cached) { setData(cached); setLoading(false) }
      else setLoading(true)

      setError(false)
      try {
        const [tRes, gcRes, vTRes, vGRes, stRes, jRes, pStRes] = await Promise.all([
          supabase.rpc('fn_classifica_tappa', { p_id_league: leagueId }).select('posizione, id_user, display_name, gap_stage').eq('id_stage', idStage).order('posizione'),
          supabase.rpc('fn_classifica_generale', { p_id_league: leagueId }).select('posizione_gc, id_user, display_name, gap_totale').eq('id_stage', idStage).order('posizione_gc'),
          supabase.rpc('fn_classifica_tappa_punti', { p_id_league: leagueId }).select('id_user, display_name, punti_verde, punti_hilly, punti_gpm').eq('id_stage', idStage),
          supabase.rpc('fn_classifica_generale_punti', { p_id_league: leagueId }).select('id_user, display_name, punti_verde_totali, punti_gpm_totali').eq('id_stage', idStage),
          // classifiche generali SENZA pari merito (spareggi in sql/20261028110000_maglie_tour_titolari.sql) e titolari delle maglie:
          // se la funzione non c'e' ancora (errore) si ricade sul calcolo di prima
          supabase.rpc('fn_tour_standings', { p_id_league: leagueId, p_id_stage: idStage }),
          supabase.rpc('fn_tour_jerseys', { p_id_league: leagueId, p_id_stage: idStage }),
          // tappa precedente: serve solo per le frecce di variazione nelle classifiche generali (se fallisce, niente frecce)
          prevStageId ? supabase.rpc('fn_tour_standings', { p_id_league: leagueId, p_id_stage: prevStageId }) : Promise.resolve({ data: null }),
        ])
        perfMark('tour_rpc_done')
        if (tRes.error || gcRes.error || vTRes.error || vGRes.error) throw (tRes.error || gcRes.error || vTRes.error || vGRes.error)

        if (cancelled) return
        const vTData = vTRes.data ?? []
        const vGData = vGRes.data ?? []
        const strict = !stRes.error && (stRes.data?.length ?? 0) > 0
        const stMap = strict ? Object.fromEntries(stRes.data.map(r => [r.id_user, r])) : {}
        // ordine della generale: posizioni 1..N dalla funzione unica; senza, il calcolo di prima (con i pari merito)
        const byPos = (rows, key) => [...rows].map(r => ({ ...r, posizione_classifica: stMap[r.id_user]?.[key] ?? 999999 })).sort((a, b) => a.posizione_classifica - b.posizione_classifica)

        // posizione precedente di ogni giocatore (null se non c'era) -> delta = posizioni guadagnate
        const prevMap = (key) => (!pStRes.error && pStRes.data ? Object.fromEntries(pStRes.data.filter(r => r[key] != null).map(r => [r.id_user, r[key]])) : null)
        const prevT = prevMap('pos_tempo'), prevPG = prevMap('pos_punti'), prevGM = prevMap('pos_gpm')
        const withDelta = (rows, prev) => rows.map(r => ({ ...r, delta: prev ? (prev[r.id_user] != null ? prev[r.id_user] - r.posizione_classifica : null) : undefined }))

        const gpmRows = vGData.map(r => ({ ...r, valore_gpm: r.punti_gpm_totali || 0 }))
        const holder = (code) => (jRes.data ?? []).find(r => r.jersey === code)?.id_user ?? null
        const result = {
          tempoT: (tRes.data ?? []).map((r, i) => ({ ...r, posizione_classifica: r.posizione ?? (i + 1) })),
          tempoG: withDelta(strict
            ? byPos(gcRes.data ?? [], 'pos_tempo').map(r => ({ ...r, posizione_gc: r.posizione_classifica }))
            : (gcRes.data ?? []).map((r, i) => ({ ...r, posizione_classifica: r.posizione_gc ?? (i + 1) })), prevT),
          puntiT: rankByPoints(vTData, r => r.punti_verde || 0),
          puntiG: withDelta(strict ? byPos(vGData, 'pos_punti') : rankByPoints(vGData, r => r.punti_verde_totali || 0), prevPG),
          gpmT: rankByPoints(vTData.map(r => ({ ...r, valore_gpm: (r.punti_gpm || 0) + (r.punti_hilly || 0) })), r => r.valore_gpm),
          gpmG: withDelta(strict ? byPos(gpmRows, 'pos_gpm') : rankByPoints(gpmRows, r => r.valore_gpm), prevGM),
          // titolari delle maglie (una a persona): solo se la funzione risponde, altrimenti la Hero usa il primo della lista come prima
          holders: strict && !jRes.error ? { tempo: holder('gc'), punti: holder('pt'), gpm: holder('gm') } : null,
        }
        setData(result)
        setLoading(false)
        cacheSet(cacheKey, result)
      } catch {
        if (!cancelled) { setError(true); setLoading(false) }
      }
    }
    load()
    return () => { cancelled = true }
  }, [idStage, prevStageId, leagueId, reloadKey])

  const hasData = Object.values(data).some(a => a.length > 0)
  const retry = () => { setLoading(true); setReloadKey(k => k + 1) }
  if (loading) return <ListSkeleton />
  if (error && !hasData) return <ErrorState onRetry={retry} />

  const isTappa = scope === 'tappa'
  // la scheda del giocatore cambia se si arriva dal tab Generale (tappa per tappa): le passo scope e tipo di classifica
  const openPlayer = (p) => onPlayerClick?.({ ...p, scope, type })
  let currentData = [], valueKey = '', labelValue = ''
  if (type === 'tempo') { currentData = isTappa ? data.tempoT : data.tempoG; valueKey = isTappa ? 'gap_stage' : 'gap_totale' }
  else if (type === 'punti') { currentData = isTappa ? data.puntiT : data.puntiG; valueKey = isTappa ? 'punti_verde' : 'punti_verde_totali'; labelValue = 'pt' }
  else if (type === 'gpm') { currentData = isTappa ? data.gpmT : data.gpmG; valueKey = 'valore_gpm'; labelValue = 'pt' }
  const rest = currentData.slice(3)
  const tied = tiedPositions(currentData)
  const valueFmt = type === 'tempo' ? (r) => fmtTime(r[valueKey]) : undefined

  // Leader della classifica generale selezionata, con la maglia di quella classifica (solo vista Generale)
  // titolare della maglia (funzione unica fn_tour_jerseys: una maglia a persona; l'azzurra puo' non esserci); senza, il primo della lista
  const holders = data.holders
  const leaderIdx = isTappa ? -1 : holders ? currentData.findIndex(r => r.id_user === holders[type]) : 0
  const leader = leaderIdx >= 0 ? currentData[leaderIdx] : null
  const second = leaderIdx >= 0 ? currentData[leaderIdx + 1] ?? null : null
  const leaderJersey = type === 'tempo' ? { color: raceColors?.gc, label: t('home.jersey_gc') }
    : type === 'punti' ? { color: raceColors?.pt, label: t('home.jersey_pt') }
      : { color: raceColors?.gm, label: t('home.jersey_gm'), pattern: raceColors?.gmPattern }
  const leaderValue = !leader ? '' : type === 'tempo' ? t('leaderboard.leader_label') : `${leader[valueKey] ?? 0} pt`
  const leaderSub = !leader || !second ? '' : type === 'tempo'
    ? t('leaderboard.lead_over_second', { gap: fmtGap(second.gap_totale) || '0:00' })
    : t('leaderboard.lead_over_second', { gap: `+${(leader[valueKey] ?? 0) - (second[valueKey] ?? 0)} pt` })

  const stageLabel = isTappa
    ? t('leaderboard.stage', { n: stageNumber }).toUpperCase()
    : t('leaderboard.general').toUpperCase()
  const typeLabelShort = type === 'tempo' ? t('leaderboard.jersey_tempo') : type === 'punti' ? t('leaderboard.jersey_punti') : t('leaderboard.jersey_gpm')

  // Immagine della classifica: disegno fisso (ClassificaShareCard), con la tua riga in fondo se sei oltre la decima
  const shareRows = currentData.map((r, i) => ({
    id_user: r.id_user,
    pos: posOf(r, i),
    name: r.display_name,
    value: valueFmt ? valueFmt(r) : `${r[valueKey] ?? 0}${labelValue ? ` ${labelValue}` : ''}`,
    tie: tied.has(posOf(r, i)),
  }))
  const handleShare = async () => {
    setSharing(true)
    try {
      await shareViewAsImage(viewShotRef, {
        fileName: `virtua-classifica-${(raceName ?? '').replace(/\s+/g, '-')}-${stageLabel.replace(/\s+/g, '-')}`,
        dialogTitle: `${raceName} — ${stageLabel}`,
      })
    } catch (e) {
      console.error('Share error:', e)
    } finally {
      setSharing(false)
    }
  }

  const jerseyBtns = [
    { id: 'tempo', label: t('leaderboard.jersey_tempo'), color: raceColors?.gc || '#FFD700' },
    { id: 'punti', label: t('leaderboard.jersey_punti'), color: raceColors?.pt || '#4CAF50' },
    { id: 'gpm', label: t('leaderboard.jersey_gpm'), color: raceColors?.gm || '#E91E63', pattern: raceColors?.gmPattern },
  ]

  return (
    <View>
      {error && <StaleBanner onRetry={retry} />}
      <View style={[styles.controls, { backgroundColor: tc.bgCard2 }]}>
      {/* Tab Tappa / Generale */}
      <SegmentedTabs
        options={[{ key: 'tappa', label: t('leaderboard.stage', { n: stageNumber }) }, { key: 'generale', label: t('leaderboard.general') }]}
        value={scope}
        onChange={setScope}
        style={{ backgroundColor: 'transparent', padding: 0 }}
      />

      {/* Bottoni maglia */}
      <View style={styles.jerseyRow} accessibilityRole="tablist">
        {jerseyBtns.map(btn => {
          const isActive = type === btn.id
          return (
            <TouchableOpacity
              key={btn.id}
              onPress={() => setType(btn.id)}
              accessibilityRole="tab"
              accessibilityState={{ selected: isActive }}
              accessibilityLabel={btn.label}
              style={[styles.jerseyBtn, { backgroundColor: isActive ? btn.color : 'transparent', borderColor: isActive ? legibleOn(btn.color, tc.bgCard2, 1.6) : 'transparent' }]}
            >
              <Text style={[styles.jerseyBtnText, { color: isActive ? textOn(btn.color) : tc.textMuted }]}>
                {btn.label.toUpperCase()}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
      </View>

      {currentData.length > 0 ? (
        <View style={[styles.card, { backgroundColor: tc.bgCard, borderColor: tc.border }]}>
          <View style={styles.cardHeader}>
            <SectionLabel>
              {isTappa ? t('leaderboard.stage_rank', { n: stageNumber }) : t('leaderboard.gc_rank')}
            </SectionLabel>
            <GradientButton
              onPress={handleShare}
              loading={sharing}
              size="sm"
              icon={<MaterialCommunityIcons name="share-variant-outline" size={13} color={tc.brandText} />}
            >
              {sharing ? t('leaderboard.sharing') : t('leaderboard.share')}
            </GradientButton>
          </View>
          <MyRank data={currentData} currentUserId={currentUserId} valueKey={valueKey} valueLabel={labelValue} valueFmt={valueFmt} tied={tied} onPlayerClick={openPlayer} numOf={type === 'tempo' ? (r) => toSeconds(r[valueKey]) : (r) => Number(r[valueKey] ?? 0)} isTime={type === 'tempo'} lowerIsBetter={type === 'tempo'} />
          {leader && (
            <LeaderHero
              userId={leader.id_user}
              name={leader.display_name}
              color={leaderJersey.color}
              label={leaderJersey.label}
              pattern={leaderJersey.pattern}
              value={leaderValue}
              sub={leaderSub}
              onPress={() => openPlayer({ id: leader.id_user, name: leader.display_name })}
            />
          )}
          <Podio data={currentData} nameKey="display_name" valueKey={valueKey} valueLabel={labelValue} currentUserId={currentUserId} userIdKey="id_user" onPlayerClick={openPlayer} valueFmt={valueFmt} tied={tied} showTrend={!isTappa} />
          {rest.length > 0 && (
            <ListaClassifica data={rest} offset={3} nameKey="display_name" valueKey={valueKey} valueLabel={labelValue} currentUserId={currentUserId} userIdKey="id_user" onPlayerClick={openPlayer} valueFmt={valueFmt} tied={tied} showTrend={!isTappa} />
          )}
        </View>
      ) : (
        <EmptyState icon="⏳" title={t('leaderboard.no_data_title')} subtitle={t('leaderboard.no_data_sub')} />
      )}

      {/* ViewShot nascosto per share */}
      <ViewShot
        ref={viewShotRef}
        options={{ format: 'png', quality: 1, pixelRatio: 3 }}
        style={{ position: 'absolute', top: -9999, left: 0, width: SHARE_W }}
      >
        <ClassificaShareCard
          raceName={raceName}
          subtitle={stageLabel}
          chip={{ color: jerseyBtns.find(b => b.id === type)?.color ?? tc.rosa, label: typeLabelShort }}
          rows={shareRows}
          currentUserId={currentUserId}
          leagueName={leagueName}
        />
      </ViewShot>
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  controls: { marginHorizontal: 16, marginBottom: 14, borderRadius: 12, padding: 4, gap: 4 },
  jerseyRow: { flexDirection: 'row', gap: 4 },
  jerseyBtn: { flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 44, paddingHorizontal: 4, borderRadius: 9, borderWidth: 2 },
  jerseyBtnText: { fontFamily: fonts.condensedBold, fontSize: 11 },
  card: { borderRadius: 14, borderWidth: 1, padding: 18, marginHorizontal: 16, marginBottom: 16 },
  cardHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 },
})